import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import ProvidersModalContextProvider from '@app/src/context/ProvidersModalContextProvider'
import ConnectProvidersModal from '@app/src/pages/ResourceCollection/Collections/Provider/ConnectProviders/ConnectProvidersModal'
import ScheduleProviderScreeningModal from '@app/src/pages/ResourceCollection/Collections/Provider/ScheduleScreening/ScheduleProviderScreeningModal'
import { RecommendationResult, RecommendationTypeName } from '@app/src/types/resourceExplorer'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Button, Card, CardContent, IconButton, Stack, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

interface OrganizationRecommendationCardProps {
  recommendations?: RecommendationResult[]
  baselineAssessmentQuestionnaireTemplateId?: number
  progressTrackerPeriod?: string
  progressTrackerGoal?: number
}

const OrganizationRecommendationCard: React.FC<OrganizationRecommendationCardProps> = props => {
  return (
    <ProvidersModalContextProvider>
      <OrganizationRecommendationCardContent {...props} />
    </ProvidersModalContextProvider>
  )
}

const OrganizationRecommendationCardContent: React.FC<OrganizationRecommendationCardProps> = ({
  recommendations = [],
  baselineAssessmentQuestionnaireTemplateId,
  progressTrackerPeriod,
  progressTrackerGoal,
}) => {
  const { handleOpenConnectDialog, handleCloseConnectDialog, isConnectDialogOpen, modalPurpose } = useProvidersModal()
  const [currentIndex, setCurrentIndex] = useState(0)
  const { formatMessage } = useIntl()

  const getActionTitle = useCallback(
    (recommendation: RecommendationResult) => {
      if (
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithLowAssessmentResult ||
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithNoPublicData ||
        recommendation.name === RecommendationTypeName.NonAiScrapedProvidersWithoutBaselineAssessmentRequest
      ) {
        return formatMessage(
          {
            id: `schemas.progressTracker.organizationRecommendation.${recommendation.name}.description`,
          },
          { count: recommendation.count },
        )
      }
      return (
        recommendation.description ||
        formatMessage({ id: 'schemas.progressTracker.organizationRecommendation.noDescription' })
      )
    },
    [formatMessage],
  )

  const getActionDescription = useCallback(
    (recommendation: RecommendationResult) => {
      if (
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithLowAssessmentResult ||
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithNoPublicData ||
        recommendation.name === RecommendationTypeName.NonAiScrapedProvidersWithoutBaselineAssessmentRequest
      ) {
        return formatMessage(
          {
            id: `schemas.progressTracker.organizationRecommendation.${recommendation.name}.detail`,
          },
          { count: recommendation.count, inScope: progressTrackerGoal, year: progressTrackerPeriod },
        )
      }
      return (
        recommendation.description ||
        formatMessage({ id: 'schemas.progressTracker.organizationRecommendation.noDescription' })
      )
    },
    [formatMessage],
  )

  const getAction = useCallback(
    (recommendation: RecommendationResult) => {
      if (
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithLowAssessmentResult ||
        recommendation.name === RecommendationTypeName.AiScrapedProvidersWithNoPublicData
      ) {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenConnectDialog(recommendation.providers, ProvidersModalPurpose.InitialInvitation)}
          >
            {formatMessage({
              id: `schemas.progressTracker.organizationRecommendation.${recommendation.name}.action`,
            })}
          </Button>
        )
      }
      if (recommendation.name === RecommendationTypeName.NonAiScrapedProvidersWithoutBaselineAssessmentRequest) {
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => handleOpenConnectDialog(recommendation.providers, ProvidersModalPurpose.ScheduleScreening)}
          >
            {formatMessage({
              id: `schemas.progressTracker.organizationRecommendation.${recommendation.name}.action`,
            })}
          </Button>
        )
      }
      return <Box />
    },
    [formatMessage, handleOpenConnectDialog, baselineAssessmentQuestionnaireTemplateId],
  )

  const handleCloseModal = () => {
    handleCloseConnectDialog()
  }

  const currentRecommendation = recommendations[currentIndex]
  const hasMultiple = recommendations.length > 1

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? recommendations.length - 1 : prev - 1))
  }

  const handleNext = () => {
    setCurrentIndex(prev => (prev === recommendations.length - 1 ? 0 : prev + 1))
  }

  if (!recommendations.length) {
    return null
  }

  return (
    <Stack spacing={1} height="100%">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" color="text.primary">
          {formatMessage({ id: 'schemas.progressTracker.organizationRecommendation.title' })}
        </Typography>
        {hasMultiple && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={handlePrevious} sx={{ color: 'text.primary' }}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1}/{recommendations.length}
            </Typography>
            <IconButton size="small" onClick={handleNext} sx={{ color: 'text.primary' }}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Card
        elevation={0}
        sx={({ palette }) => ({
          height: 'fit-content',
          background: palette.visualization[10],
          borderRadius: 2,
        })}
      >
        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1} mb={2}>
            <Typography variant="subtitle1">{getActionTitle(currentRecommendation)}</Typography>
            <Typography variant="body2">{getActionDescription(currentRecommendation)}</Typography>
          </Stack>
          <Box sx={{ mt: 'auto' }}>{getAction(currentRecommendation)}</Box>
        </CardContent>
      </Card>
      {modalPurpose === ProvidersModalPurpose.ScheduleScreening ? (
        <ScheduleProviderScreeningModal
          open={isConnectDialogOpen}
          onClose={handleCloseModal}
          preselectedTemplateId={baselineAssessmentQuestionnaireTemplateId}
        />
      ) : (
        <ConnectProvidersModal open={isConnectDialogOpen} onClose={handleCloseModal} />
      )}
    </Stack>
  )
}

export default OrganizationRecommendationCard
